/**
 * aura-tick.ts — WEB.4.UI.9a
 *
 * Pure helpers for end-of-turn Energy / Pollution production per facility.
 * Apply pattern matches `aura-build-cost.ts`:
 *   - getByScope(auraState, scope, seatIndex) → includes seat + global -1
 *   - Filter by categoryFilter (case-insensitive) + facilityFilter (case-insensitive)
 *   - Accumulate mult * mult and add + add (mult-then-add)
 *   - applyModifier(baseVal, mult, add) → Math.ceil + clamp ≥ 0
 *
 * Toll-on-landing helper deferred to UI.9b (different call site).
 */

import type { AuraInstance, AuraManagerState } from "@gdg/rules-engine";
import { applyModifier, getByScope } from "@gdg/rules-engine";
import type { AuraScope } from "@gdg/shared";
import { FACILITIES } from "./facilities.js";

export interface AuraTickBreakdown {
  baseVal: number;
  effective: number;
  /** Auras that matched this facility (for display / debugging). */
  matched: ReadonlyArray<{ source: string; mult: number; add: number }>;
}

function auraMatchesFacility(
  aura: AuraInstance,
  facilityId: string,
  facilityCategory: string,
): boolean {
  if (aura.categoryFilter != null) {
    if (aura.categoryFilter.toLowerCase() !== facilityCategory.toLowerCase()) return false;
  }
  if (aura.facilityFilter != null) {
    if (aura.facilityFilter.toLowerCase() !== facilityId.toLowerCase()) return false;
  }
  return true;
}

/**
 * Apply scoped auras (Energy or Pollution) to a facility's per-turn baseline.
 * Returns the effective per-turn value the OWNING seat receives/generates.
 */
function applyScopedAuras(
  scope: AuraScope,
  facilityId: string,
  baseVal: number,
  auraState: AuraManagerState,
  seatIndex: number,
): AuraTickBreakdown {
  const facility = FACILITIES.find((f) => f.id === facilityId);
  if (facility === undefined) {
    return { baseVal, effective: baseVal, matched: [] };
  }

  const auras = getByScope(auraState, scope, seatIndex);
  let mult = 1;
  let add = 0;
  const matched: Array<{ source: string; mult: number; add: number }> = [];

  for (const a of auras) {
    if (!auraMatchesFacility(a, facility.id, facility.category)) continue;
    const eMult = a.effect.mult !== undefined ? a.effect.mult : 1;
    const eAdd = a.effect.add ?? 0;
    mult *= eMult;
    add += eAdd;
    matched.push({ source: a.sourceCardName, mult: eMult, add: eAdd });
  }

  const { result: effective } = applyModifier(baseVal, mult, add, `Tick:${scope}`);
  return { baseVal, effective, matched };
}

/** Compute the energy a facility produces this turn, after Energy auras. */
export function computeEnergyTick(
  facilityId: string,
  auraState: AuraManagerState,
  seatIndex: number,
  /**
   * WEB.4.UI.11c — optional per-tile rule snapshot. When provided, the
   * baseline energy comes from this rather than static FACILITIES.ts so era-
   * specific values are used (e.g. coal in Era 3 Plains baseline energy = 2,
   * not the static FACILITIES coal entry's 8).
   */
  metaBaseline?: number,
): AuraTickBreakdown {
  const facility = FACILITIES.find((f) => f.id === facilityId);
  if (facility === undefined) {
    return { baseVal: 0, effective: 0, matched: [] };
  }
  const baseEnergy = metaBaseline ?? facility.energyPerTurn;
  return applyScopedAuras("Energy", facilityId, baseEnergy, auraState, seatIndex);
}

/**
 * Compute the toll an OPPONENT pays to land on this facility, after Toll auras.
 * Toll auras owned by the FACILITY OWNER scale the toll opponents must pay.
 * (WEB.4.UI.9b — Toll-on-landing query.)
 *
 * WEB.4.UI.11c: optional metaBaseline overrides static fallback.
 * R9: ownerPerk applies Politician × 1.5 multiplier after aura resolution.
 */
export function computeTollOnLanding(
  facilityId: string,
  auraState: AuraManagerState,
  ownerSeat: number,
  metaBaseline?: number,
  ownerPerk?: SeatPerk,
): AuraTickBreakdown {
  const facility = FACILITIES.find((f) => f.id === facilityId);
  if (facility === undefined) {
    return { baseVal: 0, effective: 0, matched: [] };
  }
  const baseToll = metaBaseline ?? facility.tollPerLand;
  const breakdown = applyScopedAuras("Toll", facilityId, baseToll, auraState, ownerSeat);

  // R9 perk: Politician — toll income × 1.5 (ceil)
  if (ownerPerk === "politician") {
    const boosted = Math.ceil(breakdown.effective * 1.5);
    return { ...breakdown, effective: boosted };
  }
  return breakdown;
}

/**
 * Compute pollution a facility generates this turn, after Pollution auras.
 * WEB.4.UI.11c: optional metaBaseline overrides static fallback.
 */
export function computePollutionTick(
  facilityId: string,
  auraState: AuraManagerState,
  seatIndex: number,
  metaBaseline?: number,
): AuraTickBreakdown {
  const facility = FACILITIES.find((f) => f.id === facilityId);
  if (facility === undefined) {
    return { baseVal: 0, effective: 0, matched: [] };
  }
  const basePol = metaBaseline ?? facility.pollutionPerTurn;
  return applyScopedAuras("Pollution", facilityId, basePol, auraState, seatIndex);
}

/** R9 perk archetype for a seat — drives per-perk modifiers below. */
export type SeatPerk = "engineer" | "scientist" | "activist" | "politician";

/**
 * Aggregate end-of-turn deltas across ALL facilities owned by the given seat.
 * Returns total energy + pollution to add to that seat's resources.
 *
 * @param builtFacilities — tileIdx → facilityId
 * @param ownedBy         — tileIdx → seat
 * @param auraState       — active aura manager state
 * @param seat            — the seat whose turn is ending
 * @param meta            — WEB.4.UI.11c per-tile FacilityRule snapshot (optional)
 * @param perk            — R9 archetype perk for this seat (optional, default no perk)
 *
 * R9 perk effects applied here:
 *   Engineer  — pollution +1 per owned facility (applied per-facility after aura)
 *   Scientist — renewable energy +1 per owned renewable facility (applied per-facility)
 *   Activist  — facility pollution × 0.7 (applied per-facility; rounded down, floor 0)
 */
export function computeTurnEndDeltas(
  builtFacilities: Record<number, string | undefined>,
  ownedBy: Record<number, number | undefined>,
  auraState: AuraManagerState,
  seat: number,
  /**
   * WEB.4.UI.11c — per-tile FacilityRule snapshot lookup (optional).
   * When provided, tick uses meta.energyPerTurn / meta.pollutionPerTurn
   * instead of static FACILITIES.ts fallback.
   */
  meta?: Record<
    number,
    { energyPerTurn: number; pollutionPerTurn: number; tollPerLand: number } | undefined
  >,
  perk?: SeatPerk,
): { energy: number; pollution: number } {
  let energy = 0;
  let pollution = 0;
  for (const [tileIdxStr, facilityId] of Object.entries(builtFacilities)) {
    if (facilityId === undefined) continue;
    const tileIdx = Number(tileIdxStr);
    if (ownedBy[tileIdx] !== seat) continue;
    const tileMeta = meta?.[tileIdx];

    // Base energy tick (aura-modified)
    let facilityEnergy = computeEnergyTick(
      facilityId,
      auraState,
      seat,
      tileMeta?.energyPerTurn,
    ).effective;
    // Base pollution tick (aura-modified)
    let facilityPollution = computePollutionTick(
      facilityId,
      auraState,
      seat,
      tileMeta?.pollutionPerTurn,
    ).effective;

    // R9 perk: Scientist — renewable energy +1 per owned renewable
    if (perk === "scientist") {
      const def = FACILITIES.find((f) => f.id === facilityId);
      if (def?.category === "Renewable") facilityEnergy += 1;
    }

    // R9 perk: Activist — facility pollution × 0.7 (floor 0)
    if (perk === "activist") {
      facilityPollution = Math.max(0, Math.floor(facilityPollution * 0.7));
    }

    // R9 perk: Engineer — pollution +1 per owned facility (additional burden of industrial output)
    if (perk === "engineer") {
      facilityPollution += 1;
    }

    energy += facilityEnergy;
    pollution += facilityPollution;
  }

  // R9 perk: Scientist — flat pollution +1 per turn (science has industrial side-effects)
  if (perk === "scientist") pollution += 1;

  return { energy, pollution };
}
